import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiUrl } from "../config/api";
import { useBreakpoint } from "../utils/useMediaQuery";

import GeneralSheet from "./sheets/GeneralSheet";
import StaffSheet from "./sheets/StaffSheet";
import TmcSheet from "./sheets/TmcSheet";
import AmortSheet from "./sheets/AmortSheet";
import ExtrasSheet from "./sheets/ExtrasSheet";
import SummarySheet from "./sheets/SummarySheet";
import GroupPushModal from "./components/GroupPushModal";

import "../styles/brand-decor.css";

const SHEETS = [
  "Общая информация",
  "Штатное расписание",
  "ТМЦ",
  "Амортизация",
  "Доп.затраты",
  "Итоговая страница",
];
const SHEET_STORAGE_KEY = "cardCalc:lastSheet";
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const buildAmortDeliveryKey = (item = {}) => {
  if (!item) return "";
  if (item._deliveryKey) return String(item._deliveryKey);
  if (item.tmc_delivery_key) return String(item.tmc_delivery_key);
  if (item.tmc_id != null && item.tmc_id !== "") return `tmc:${item.tmc_id}`;
  const name = String(item.name || "").trim().toLowerCase();
  if (!name) return "";
  const priceKey = Math.round((Number(item.price) || 0) * 100);
  return `name:${name}:${priceKey}`;
};
const extractDeliveryMarker = (row = {}) => {
  if (!row || typeof row !== "object") return null;
  return row._deliveryKey || row.delivery_key || row.tmc_delivery_key || null;
};

function getEmptyCardData() {
  return {
    objectCode: "",
    status: "draft",
    createdAt: new Date().toISOString(),
    approvals: [],
    contractType: 'stationary',
    signedAt: null,
    signedBy: null,
    signedFrom: null,
    groupId: null,
    groupName: '',
    groupCode: '',
    objectName: "",
    company: "",
    address: "",
    customer: "",
    comment: "",
    areaTable: [],
    sheets: {
      general: {},
      staff: [],
      staffMeta: {},
      tmc: [],
      amortization: [],
      extraCosts: [],
      summary: {},
    },
  };
}

function mergeCardWithEmpty(data) {
  const base = getEmptyCardData();
  const incomingSheets = data?.sheets || {};
  const incomingExtraCosts = Array.isArray(incomingSheets.extraCosts)
    ? incomingSheets.extraCosts
    : Array.isArray(incomingSheets.extras)
    ? incomingSheets.extras
    : [];

  return {
    ...base,
    ...data,
    contractType: data?.contractType || base.contractType,
    areaTable: Array.isArray(data?.areaTable) ? data.areaTable : base.areaTable,
    sheets: {
      ...base.sheets,
      ...incomingSheets,
      staff: Array.isArray(incomingSheets?.staff) ? incomingSheets.staff : [],
      staffMeta: (incomingSheets?.staffMeta && typeof incomingSheets.staffMeta === 'object')
        ? incomingSheets.staffMeta
        : base.sheets.staffMeta,
      tmc: Array.isArray(incomingSheets?.tmc) ? incomingSheets.tmc : [],
      amortization: Array.isArray(incomingSheets?.amortization) ? incomingSheets.amortization : [],
      extraCosts: incomingExtraCosts,
      summary: incomingSheets?.summary || {},
    },
  };
}

export default function CardCalc({ isNew = false }) {
  const { id } = useParams(); // objectCode из URL
  const navigate = useNavigate();

  const cardRef = useRef(null);
  const [cardData, setCardData] = useState(getEmptyCardData());
  const [activeSheet, setActiveSheet] = useState(() => {
    if (typeof window === "undefined") return 0;
    const stored = Number(window.localStorage.getItem(SHEET_STORAGE_KEY));
    return Number.isInteger(stored) && stored >= 0 && stored < SHEETS.length ? stored : 0;
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAdmin] = useState(() => localStorage.getItem("isAdmin") === "1");
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || 'null'); } catch { return null; }
  });
  const isSuper = ['1001','1002'].includes(String(user?.id));
  const [objectLinks, setObjectLinks] = useState([]);
  const [objectLinksLoading, setObjectLinksLoading] = useState(false);
  const [objectLinksError, setObjectLinksError] = useState('');
  // После подписания блокируем редактирование для всех пользователей
  const locked = !!(cardData?.signedAt);
  const isCompactLayout = useBreakpoint('lg');
  const [pushModal, setPushModal] = useState({ open: false, sheetKeys: [] });

  const loadObjectLinks = useCallback(async (targetObjectId) => {
    const normalizedId = Number(targetObjectId);
    if (!normalizedId) {
      setObjectLinks([]);
      setObjectLinksError('');
      setObjectLinksLoading(false);
      return;
    }
    setObjectLinksLoading(true);
    setObjectLinksError('');
    try {
      const resp = await fetch(apiUrl(`/api/objects/${encodeURIComponent(normalizedId)}/links`));
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      setObjectLinks(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('load object links failed', e);
      setObjectLinks([]);
      setObjectLinksError('Не удалось загрузить ссылки объекта');
    } finally {
      setObjectLinksLoading(false);
    }
  }, []);

  const handleLinksReload = useCallback((targetId) => {
    return loadObjectLinks(targetId ?? cardData?.objectId);
  }, [cardData?.objectId, loadObjectLinks]);

  function shortFio(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const fam = parts[0];
    const i = parts[1] ? (parts[1][0] + '.') : '';
    const o = parts[2] ? (parts[2][0] + '.') : '';
    return [fam, i, o].filter(Boolean).join(' ');
  }

  // дебаунс сохранения
  const saveTimerRef = useRef(null);
  const pendingCommitRef = useRef(false);
  const latestDataRef = useRef(getEmptyCardData());
  const SAVE_DELAY = 700;

  // Загрузка карточки
  useEffect(() => {
    let aborted = false;

    async function load() {
      if (isNew) {
        setCardData(getEmptyCardData());
        setLoading(false);
        return;
      }
      if (!id) {
        setCardData(getEmptyCardData());
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(id)}`));
        if (aborted) return;
        if (res.status === 404) {
          setCardData({ notFound: true });
        } else {
          const data = await res.json();
          setCardData(data ? mergeCardWithEmpty(data) : { notFound: true });
        }
      } catch {
        if (!aborted) setCardData({ notFound: true });
      } finally {
        if (!aborted) setLoading(false);
      }
    }

    load();
    return () => {
      aborted = true;
    };
  }, [id, isNew]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    latestDataRef.current = cardData;
  }, [cardData]);

  useEffect(() => {
    loadObjectLinks(cardData?.objectId);
  }, [cardData?.objectId, loadObjectLinks]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SHEET_STORAGE_KEY, String(activeSheet));
  }, [activeSheet]);

  const runPatchSave = useCallback(async () => {
    const payload = latestDataRef.current;
    if (!payload?.objectCode) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaving(true);
    try {
      const body = JSON.stringify({ ...payload, objectCode: payload.objectCode });
      const res = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(payload.objectCode)}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body,
      });
      if (!res.ok) {
        let errText = "";
        try { errText = await res.text(); } catch {}
        try {
          const parsed = JSON.parse(errText || "{}");
          if (parsed?.error) errText = parsed.error;
        } catch {}
        throw new Error(errText || `HTTP ${res.status}`);
      }
      const updatedCard = await res.json();
      const clientSheets = payload?.sheets || {};
      const serverSheets = updatedCard?.sheets || {};

      const safeUpdated = mergeCardWithEmpty({
        ...updatedCard,
        sheets: {
          ...serverSheets,
          staff:
            Array.isArray(serverSheets.staff) && serverSheets.staff.length > 0
              ? serverSheets.staff
              : Array.isArray(clientSheets.staff)
              ? clientSheets.staff
              : [],
          tmc:
            Array.isArray(serverSheets.tmc) && serverSheets.tmc.length > 0
              ? serverSheets.tmc
              : Array.isArray(clientSheets.tmc)
              ? clientSheets.tmc
              : [],
          amortization:
            Array.isArray(serverSheets.amortization) && serverSheets.amortization.length > 0
              ? serverSheets.amortization
              : Array.isArray(clientSheets.amortization)
              ? clientSheets.amortization
              : [],
          extraCosts:
            Array.isArray(serverSheets.extraCosts) && serverSheets.extraCosts.length > 0
              ? serverSheets.extraCosts
              : Array.isArray(clientSheets.extraCosts)
              ? clientSheets.extraCosts
              : [],
          summary:
            serverSheets.summary && Object.keys(serverSheets.summary).length > 0
              ? serverSheets.summary
              : clientSheets.summary || {},
        },
      });

      latestDataRef.current = safeUpdated;
      setCardData(safeUpdated);
    } catch (err) {
      console.error("PATCH /api/card-calc failed", err);
      const msg = String(err?.message || err || "").toLowerCase();
      if (msg.includes("signed_locked")) {
        alert(
          "Сохранение заблокировано: после подписания редактирование ограничено. Доступны только разрешённые поля для суперпользователей."
        );
      }
    } finally {
      setSaving(false);
    }
  }, []);

  const scheduleSave = useCallback((immediate = false) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (immediate) {
      runPatchSave();
      return;
    }
    saveTimerRef.current = setTimeout(() => {
      runPatchSave();
    }, SAVE_DELAY);
  }, [runPatchSave, SAVE_DELAY]);

  useEffect(() => {
    function handleFocusOut(event) {
      if (!cardRef.current || !cardRef.current.contains(event.target)) return;
      // Defer until blur handlers mark pending commits; otherwise capture-phase fires too early
      setTimeout(() => {
        if (!pendingCommitRef.current) return;
        pendingCommitRef.current = false;
        scheduleSave(false);
      }, 0);
    }
    document.addEventListener("focusout", handleFocusOut, true);
    return () => document.removeEventListener("focusout", handleFocusOut, true);
  }, [scheduleSave]);

  // Первое сохранение (создание)
  function handleFirstSaveCard(newData) {
    fetch(apiUrl('/api/card-calc'), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newData),
    })
      .then((res) => res.json())
      .then((created) => {
        const merged = mergeCardWithEmpty({ ...newData, ...created });
        setCardData(merged);
        const code = created?.objectCode || merged.objectCode;
        if (code) navigate(`/card-calc/${code}`);
      })
      .catch((err) => alert("Ошибка создания карточки: " + err));
  }

  // Унифицированное автосохранение: фиксируем изменения сразу, но отправляем PATCH только после ухода из поля
  const handleAutoSave = useCallback((nextData, options = {}) => {
    const normalized = mergeCardWithEmpty(nextData);
    setCardData(normalized);
    latestDataRef.current = normalized;

    const code = normalized?.objectCode || cardData?.objectCode;
    if (!code) return;

    if (options?.forceCommit) {
      pendingCommitRef.current = false;
      scheduleSave(true);
      return;
    }

    pendingCommitRef.current = true;
  }, [cardData, scheduleSave]);

  useEffect(() => {
    if (!cardData?.objectCode || locked) return;
    const deliveredAmort = Array.isArray(cardData.deliveredAmortization) ? cardData.deliveredAmortization : [];
    if (!deliveredAmort.length) return;
    const amortRows = Array.isArray(cardData.sheets?.amortization) ? cardData.sheets.amortization : [];
    const existingDeliveryKeys = new Set(
      amortRows
        .map((row) => extractDeliveryMarker(row))
        .filter(Boolean)
    );
    const additions = [];
    deliveredAmort.forEach((item) => {
      const key = buildAmortDeliveryKey(item);
      if (!key || existingDeliveryKeys.has(key)) return;
      const qty = Number(item.delivered_qty) || 0;
      const price = Number(item.price) || 0;
      if (qty <= 0 || price <= 0) return;
      const total = round2(qty * price);
      const normRaw = Number(item.norm_months) || Number(item.amortization_period) || 12;
      const months = normRaw > 0 ? Math.round(normRaw) : 12;
      additions.push({
        tmc_id: item.tmc_id ?? null,
        name: item.name || "",
        code: item.code || "",
        unit: item.unit || "",
        price,
        qty,
        total,
        supplier_link: item.supplier_link || "",
        norm_months: months,
        amort_per_month: months > 0 ? round2(total / months) : 0,
        _deliveryKey: key,
        source: "delivered",
      });
      existingDeliveryKeys.add(key);
    });
    if (!additions.length) return;
    const next = {
      ...cardData,
      sheets: {
        ...cardData.sheets,
        amortization: [...amortRows, ...additions],
      },
    };
    handleAutoSave(next, { forceCommit: true });
  }, [cardData, locked, handleAutoSave]);

  const handleEditPositions = () =>
    alert("Откроется редактор справочника Должностей (доступно администратору).");
  const handleEditRegimes = () =>
    alert("Откроется редактор справочника Режимов (доступно администратору).");

  const openGroupPush = (sheetKey) => {
    if (!cardData?.groupId) {
      alert('Карточка не состоит в группе, нечего синхронизировать');
      return;
    }
    setPushModal({ open: true, sheetKeys: sheetKey ? [sheetKey] : [] });
  };

  if (loading) {
    return <div style={{ textAlign: "center", margin: 40 }}>Загрузка…</div>;
  }
  if (cardData && cardData.notFound) {
    return (
      <div style={{ color: "#c00", textAlign: "center", margin: 40 }}>
        Карточка с таким кодом не найдена.
      </div>
    );
  }

  const cardTitle = (
    <span style={{ fontWeight: "normal" }}>
      Карточка расчёта стоимости услуг объекта{" "}
      <span style={{ fontWeight: "bold" }}>«{cardData?.objectName || "—"}»</span>
    </span>
  );
  const groupLinkText = cardData?.groupName || cardData?.groupCode || (cardData?.groupId ? `Группа #${cardData.groupId}` : '');

  async function signCard() {
    if (!cardData?.objectCode) return;
    try {
      const resp = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(cardData.objectCode)}/sign`), {
        method: 'POST'
      });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      setCardData(mergeCardWithEmpty(data));
    } catch (e) {
      alert('Ошибка подписания: ' + (e?.message || e));
    }
  }

  async function unsignCard() {
    if (!cardData?.objectCode) return;
    if (!isSuper) { alert('Снятие подписи доступно только пользователям 1001/1002'); return; }
    if ((cardData?.contractType || 'stationary') === 'stationary') {
      alert('Снятие подписи для стационарных карточек выполняется только из группы');
      return;
    }
    try {
      const resp = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(cardData.objectCode)}/unsign`), {
        method: 'POST'
      });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      setCardData(mergeCardWithEmpty(data));
    } catch (e) {
      alert('Ошибка снятия подписи: ' + (e?.message || e));
    }
  }

  async function actualizeCard(force = false) {
    if (!isSuper) { alert('Актуализация доступна только пользователям 1001/1002'); return; }
    if (!cardData?.objectCode) return;
    try {
      const url = new URL(apiUrl(`/api/card-calc/${encodeURIComponent(cardData.objectCode)}/actualize`));
      if (force) url.searchParams.set('force', '1');
      const resp = await fetch(url.toString(), { method: 'POST' });
      const data = await resp.json().catch(()=>null);
      if (resp.status === 409 && data?.error === 'actual_exists') {
        const ok = window.confirm(`Актуальная карточка за этот месяц уже есть (код ${data.conflict?.objectCode}). Заменить?`);
        if (ok) return actualizeCard(true);
        return;
      }
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      setCardData(mergeCardWithEmpty(data));
    } catch (e) {
      alert('Ошибка актуализации: ' + (e?.message || e));
    }
  }

  // Доп. панель действий для отдельных вкладок
  return (
    <div className="brand-page">
      <div
        className={`calc-card calc-card--brand${isCompactLayout ? " calc-card--compact" : ""}`}
        ref={cardRef}
        style={{
          background: "var(--bg-card, #fff)",
          border: "1px solid var(--border-200, #e6e8ef)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-1, 0 2px 16px rgba(0,0,0,.08))",
          maxWidth: isCompactLayout ? "100%" : 1700,
          width: "100%",
          margin: isCompactLayout ? "8px auto 16px" : "16px auto 24px",
          padding: isCompactLayout ? 14 : 22,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 10,
            zIndex: 5,
            background: "var(--bg-card, #fff)",
            paddingBottom: 12,
            borderBottom: "1px solid rgba(84,39,176,0.12)",
            marginBottom: 16,
          }}
        >
          {cardData?.objectCode && (
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "var(--text-700, #2b2f36)",
                textAlign: "center",
                marginBottom: 6,
              }}
            >
              Код: {cardData?.objectCodeFull || (cardData?.objectCode ? String(cardData.objectCode).padStart(4,'0') + '-0' : '—')}
            </div>
          )}

          <div
            className="calc-title"
            style={{
              fontWeight: "normal",
              fontSize: "1.35rem",
              textAlign: "center",
              margin: "4px 0 12px 0",
              lineHeight: "1.2",
              color: "var(--text-900, #111317)",
            }}
          >
            {cardTitle}
          </div>

          {cardData?.groupId && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 8,
                alignItems: 'center',
                marginBottom: 14,
                fontSize: '0.95rem',
                color: '#475569',
              }}
            >
              <span style={{ fontWeight: 600 }}>В группе:</span>
              <a
                href={`/groups/${cardData.groupId}`}
                target="_blank"
                rel="noreferrer noopener"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: '1px solid #cbd5f5',
                  background: '#f8fafc',
                  textDecoration: 'none',
                  color: '#1d4ed8',
                  fontWeight: 600,
                }}
                title="Открыть группу в новой вкладке"
              >
                {groupLinkText}
                <span aria-hidden="true" style={{ fontSize: 16 }}>↗</span>
              </a>
              {!locked && (
                <button
                  type="button"
                  onClick={() => openGroupPush()}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: '1px solid #d4d8f0',
                    background: '#eef2ff',
                    color: '#4338ca',
                    fontWeight: 600,
                  }}
                >
                  Скопировать в другие
                </button>
              )}
            </div>
          )}

          {cardData?.objectId && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'center',
                marginBottom: 18,
              }}
            >
              <div style={{ fontWeight: 600, color: '#475569' }}>Ссылки по объекту</div>
              {objectLinksLoading ? (
                <div style={{ fontSize: 14, color: '#94a3b8' }}>Загрузка…</div>
              ) : objectLinksError ? (
                <div style={{ fontSize: 14, color: '#b91c1c' }}>{objectLinksError}</div>
              ) : objectLinks.length ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    justifyContent: 'center',
                  }}
                >
                  {objectLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 999,
                        border: '1px solid #d7e3fc',
                        background: '#f8fbff',
                        color: '#1d4ed8',
                        textDecoration: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {link.title || link.url}
                      <span aria-hidden="true" style={{ fontSize: 14 }}>↗</span>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 14, color: '#94a3b8' }}>Ссылки для объекта ещё не добавлены.</div>
              )}
            </div>
          )}

        {/* Подписание */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center',
          marginBottom: 18, padding: '10px 12px', border: '1px dashed #c59bff', borderRadius: 10,
          background: '#faf7ff'
        }}>
          {cardData?.signedAt ? (
            <>
              <span style={{ fontWeight: 700, color: '#2c7a2c' }}>Подписано</span>
              <span style={{ color: '#444' }}>{new Date(cardData.signedAt).toLocaleString()}</span>
              <span style={{ color: '#666' }}>({shortFio(cardData.signedBy) || '—'}{cardData.signedFrom ? `, ${cardData.signedFrom}` : ''})</span>
              {isSuper && (cardData.contractType === 'one-time') && (
                <button onClick={unsignCard} style={{ marginLeft: 12, padding: '6px 10px', borderRadius: 6 }}>
                  Снять подпись
                </button>
              )}
            </>
          ) : (
            <>
              <span style={{ fontWeight: 700, color: '#8b5cf6' }}>Не подписано</span>
              <span style={{ color: '#666' }}>Вид контракта: {cardData.contractType === 'one-time' ? 'Разовый' : 'Стационар'}</span>
              {cardData.contractType === 'one-time' ? (
                <button onClick={signCard} style={{ marginLeft: 12, padding: '6px 10px', borderRadius: 6, background: '#e7f6d4', border: '1px solid #8bc34a' }}>
                  Подписать
                </button>
              ) : (
                <span style={{ color: '#777' }}>Подписание для стационарных — из группы</span>
              )}
            </>
          )}
          {/* Актуализация */}
          <span style={{ marginLeft: 12, color: '#6b7280' }}>|</span>
          {cardData?.actualAt ? (
            <>
              {(() => {
                const m = Number(cardData?.periodMonth);
                const y = Number(cardData?.periodYear);
                let dt = null;
                if (m && y) dt = new Date(y, m - 1, 1);
                else if (cardData?.actualAt) dt = new Date(cardData.actualAt);
                let monthYear = '';
                if (dt && !isNaN(dt)) {
                  const mName = dt.toLocaleString('ru-RU', { month: 'long' });
                  const cap = mName.charAt(0).toUpperCase() + mName.slice(1);
                  monthYear = `${cap}, ${dt.getFullYear()}`;
                }
                return (
                  <span style={{ color: '#2563eb', fontWeight: 600 }}>
                    Актуально{monthYear ? ` ${monthYear}` : ''}
                  </span>
                );
              })()}
              <span style={{ color: '#444' }}>{new Date(cardData.actualAt).toLocaleString()}</span>
              <span style={{ color: '#666' }}>({shortFio(cardData.actualBy) || '—'})</span>
            </>
          ) : (
            // Кнопка актуализации недоступна для стационарных расчётов — только через группу
            (isSuper && cardData.contractType === 'one-time') && (
              <button onClick={()=>actualizeCard(false)} style={{ padding: '6px 10px', borderRadius: 6, background: '#e0e7ff', border: '1px solid #93c5fd' }}>
                Актуализировать
              </button>
            )
          )}
        </div>

          {/* Вкладки */}
          <div
            className={isCompactLayout ? "calc-tabs-scroll" : undefined}
            style={{
              display: "flex",
              justifyContent: isCompactLayout ? "flex-start" : "center",
              marginBottom: 6,
              gap: 10,
              flexWrap: isCompactLayout ? "nowrap" : "wrap",
              overflowX: isCompactLayout ? "auto" : "visible",
              paddingBottom: isCompactLayout ? 6 : 0,
            }}
          >
          {SHEETS.map((name, idx) => {
            const active = activeSheet === idx;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setActiveSheet(idx)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  background: active ? "var(--brand-50, #f5efff)" : "#fff",
                  border: active ? "1px solid var(--brand-300, #c59bff)" : "1px solid var(--border-200, #e6e8ef)",
                  color: active ? "var(--brand-800, #5427b0)" : "var(--text-700, #2b2f36)",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: active ? "0 2px 10px rgba(0,0,0,.06)" : "none",
                  flex: isCompactLayout ? "0 0 auto" : undefined,
                  minWidth: isCompactLayout ? 180 : undefined,
                }}
              >
                {name}
              </button>
            );
          })}
          </div>
          {isCompactLayout && (
            <div className="mobile-scroll-hint">Свайпните вкладки, чтобы открыть нужный лист</div>
          )}
        </div>

        {/* Контент вкладок */}
        <div style={{ position: 'relative', marginBottom: 8, opacity: locked ? 0.7 : 1 }}>
          {/* Без перекрывающего оверлея: оставляем полную навигацию/выделение текста. 
              Блокировка достигается тем, что все поля на листах уважают readOnlyLocked. */}
          {activeSheet === 0 && (
            <GeneralSheet
              data={cardData}
              readOnlyLocked={locked}
              objectLinks={objectLinks}
              objectLinksLoading={objectLinksLoading}
              objectLinksError={objectLinksError}
              onObjectLinksChange={setObjectLinks}
              onRequestLinksReload={handleLinksReload}
              onChange={cardData?.objectCode ? (locked ? undefined : handleAutoSave) : handleFirstSaveCard}
            />
          )}

          {activeSheet === 1 && (
            <StaffSheet
              data={cardData.sheets?.staff || []}
              printMeta={{
                objectName: cardData?.objectName || '',
                objectCode: cardData?.objectCodeFull || (cardData?.objectCode ? String(cardData.objectCode).padStart(4,'0') + '-0' : ''),
                periodMonth: cardData?.periodMonth,
                periodYear: cardData?.periodYear,
                actualAt: cardData?.actualAt,
              }}
              readOnlyLocked={locked}
              onChange={(v, opts) => handleAutoSave({
                  ...cardData,
                  sheets: { ...cardData.sheets, staff: v },
                }, opts)}
              isAdmin={isAdmin}
              onEditPositions={handleEditPositions}
              onEditRegimes={handleEditRegimes}
              isSuper={isSuper}
              advancesComment={cardData.sheets?.staffMeta?.advancesComment || ''}
              onAdvancesCommentChange={(value, opts) => handleAutoSave({
                  ...cardData,
                  sheets: {
                    ...cardData.sheets,
                    staffMeta: {
                      ...(cardData.sheets?.staffMeta || {}),
                      advancesComment: value,
                    },
                  },
                }, opts)}
              extraActions={!locked && cardData?.groupId ? (
                <button className="sheet-action" type="button" onClick={() => openGroupPush('staff')}>
                  Применить к группе
                </button>
              ) : null}
            />
          )}

          {activeSheet === 2 && (
            <TmcSheet
              data={cardData.sheets?.tmc || []}
              delivered={cardData.deliveredTmc || []}
              readOnlyLocked={locked}
              onChange={(v, opts) => !locked && handleAutoSave({
                  ...cardData,
                  sheets: { ...cardData.sheets, tmc: v },
                }, opts)}
              extraActions={!locked && cardData?.groupId ? (
                <button className="sheet-action" type="button" onClick={() => openGroupPush('tmc')}>
                  Применить к группе
                </button>
              ) : null}
            />
          )}

          {activeSheet === 3 && (
            <AmortSheet
              data={cardData.sheets?.amortization || []}
              readOnlyLocked={locked}
              onChange={(v, opts) => !locked && handleAutoSave({
                  ...cardData,
                  sheets: { ...cardData.sheets, amortization: v },
                }, opts)}
              extraActions={!locked && cardData?.groupId ? (
                <button className="sheet-action" type="button" onClick={() => openGroupPush('amortization')}>
                  Применить к группе
                </button>
              ) : null}
            />
          )}

          {activeSheet === 4 && (
            <ExtrasSheet
              data={cardData.sheets?.extraCosts || []}
              readOnlyLocked={locked}
              onChange={(v, opts) => handleAutoSave({
                  ...cardData,
                  sheets: { ...cardData.sheets, extraCosts: v },
                }, opts)}
              isSuper={isSuper}
              extraActions={!locked && cardData?.groupId ? (
                <button className="sheet-action" type="button" onClick={() => openGroupPush('extraCosts')}>
                  Применить к группе
                </button>
              ) : null}
            />
          )}

          {activeSheet === 5 && (
            <SummarySheet
              cardData={cardData}
              deliveredTotals={cardData.deliveredTotals || {}}
              readOnlyLocked={locked}
              onChange={locked ? undefined : handleAutoSave}
              extraActions={!locked && cardData?.groupId ? (
                <button className="sheet-action" type="button" onClick={() => openGroupPush('summary')}>
                  Применить к группе
                </button>
              ) : null}
            />
          )}
        </div>

        <div style={{ marginTop: 6, fontSize: 12, color: saving ? "#0070f3" : "#6b7280", textAlign: "right" }}>
          {locked ? "Карточка подписана и недоступна для редактирования" : (saving ? "Сохранение…" : "Все изменения сохраняются автоматически")}
        </div>
      </div>
      <GroupPushModal
        open={pushModal.open}
        defaultSheetKeys={pushModal.sheetKeys}
        onClose={() => setPushModal({ open: false, sheetKeys: [] })}
        cardCode={cardData?.objectCode}
        cardTitle={cardData?.objectName || cardData?.objectCodeFull || ''}
      />
    </div>
  );
}